import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { LANGUAGES } from "@/i18n";

export default function LanguageSwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const pick = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        data-testid="lang-toggle"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1 text-[11px] font-body font-medium text-bark/70 hover:text-falu transition-colors uppercase tracking-wider px-2 py-1 rounded-sm ${open ? "bg-bone-alt" : ""}`}
        aria-label={t("language.label")}
      >
        <Globe size={12} />
        <span className="hidden sm:inline">{compact ? current.code.toUpperCase() : current.label}</span>
        <span className="sm:hidden">{current.code.toUpperCase()}</span>
        <ChevronDown size={11} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-parchment border border-warm-border rounded-sm shadow-lg min-w-[180px] max-h-[360px] overflow-y-auto z-50 py-1">
          <div className="px-3 py-2 border-b border-warm-border/50">
            <p className="text-[10px] font-semibold text-bark/50 uppercase tracking-wider">{t("language.label")}</p>
          </div>
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => pick(l.code)}
              className={`w-full text-left px-3 py-2 text-sm font-body flex items-center justify-between gap-2 hover:bg-bone-alt transition-colors ${l.code === i18n.language ? "font-semibold text-falu" : "text-ink"}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-base">{l.flag}</span>
                <span>{l.label}</span>
              </span>
              {l.code === i18n.language && <Check size={13} className="text-falu" />}
            </button>
          ))}
          <div className="px-3 py-2 border-t border-warm-border/50">
            <p className="text-[10px] text-bark/55 leading-relaxed italic">{t("language.ui_note")}</p>
          </div>
        </div>
      )}
    </div>
  );
}
